import React, {useState, useEffect} from 'react';
import {
  IconButton,
  Badge,
  Menu,
  MenuItem,
  ListItemText,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  Snackbar,
  Alert,
  AlertColor,
  Box,
  useTheme,
  Fade,
  Grow,
  Slide,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import {TFunction} from 'i18next';
import {
  GetUserNotificationListDto,
  UserNotificationDto,
} from '../../api/dto/userNotification';
import {
  getUserMeNotificationList,
  patchUserNotification,
  deleteUserNotification,
} from '../../api/userNotification';
import {ALERT_HIDE_DURATION} from '../../constant';

const NotificationButton = ({t}: {t: TFunction<'translation', undefined>}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<
    Array<UserNotificationDto>
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedNotification, setSelectedNotification] =
    useState<UserNotificationDto | null>(null);
  const [selectedDeleteNotification, setSelectedDeleteNotification] =
    useState<UserNotificationDto | null>(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [showSnackbarType, setShowSnackbarType] =
    useState<AlertColor>('success');
  const [showSnackbarMessage, setShowSnackbarMessage] = useState<string>();

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const handleNotificationClick = async (notification: UserNotificationDto) => {
    setSelectedNotification(notification);

    if (!notification.isRead) {
      try {
        await patchUserNotification(notification.id, {isRead: true});
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? {...n, isRead: true} : n)),
        );
      } catch (error) {
        setShowSnackbarType('error');
        setShowSnackbarMessage(JSON.stringify(error));
        setShowSnackbar(true);
      }
    }
  };

  const handleDeleteClick = (notification: UserNotificationDto) => {
    setSelectedDeleteNotification(notification);
    setSelectedNotification(null);
  };

  const handleConfirmDelete = async () => {
    if (selectedDeleteNotification) {
      try {
        await deleteUserNotification(selectedDeleteNotification.id);
        setNotifications(prev =>
          prev.filter(n => n.id !== selectedDeleteNotification.id),
        );
      } catch (error) {
        setShowSnackbarType('error');
        setShowSnackbarMessage(JSON.stringify(error));
        setShowSnackbar(true);
      } finally {
        setSelectedDeleteNotification(null);
      }
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data: GetUserNotificationListDto = {
        orderBy: 'createdAt',
        sort: 'DESC',
      };
      const response = await getUserMeNotificationList(data);
      setNotifications(response?.data?.items ?? []);
    } catch (error) {
      setShowSnackbarType('error');
      setShowSnackbarMessage(JSON.stringify(error));
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  return (
    <>
      <IconButton
        color='inherit'
        onClick={handleClick}
        sx={{
          position: 'relative',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'scale(1.1)',
            '&::after': {
              transform: 'scale(1.2)',
              opacity: 0.1,
            },
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: '50%',
            backgroundColor: theme.palette.primary.main,
            opacity: 0,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          },
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color='warning'
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: theme.palette.warning.main,
              color: theme.palette.warning.contrastText,
              boxShadow: '0 0 12px rgba(0,0,0,0.2)',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  boxShadow: '0 0 0 0 rgba(0,0,0,0.2)',
                },
                '70%': {
                  boxShadow: '0 0 0 8px rgba(0,0,0,0)',
                },
                '100%': {
                  boxShadow: '0 0 0 0 rgba(0,0,0,0)',
                },
              },
            },
          }}
        >
          <NotificationsIcon sx={{fontSize: 28}} />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        TransitionComponent={Grow}
        transitionDuration={200}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '20px',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: theme.palette.text.primary,
            maxHeight: '60vh',
            overflow: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0,0,0,0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(0,0,0,0.1)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(0,0,0,0.2)',
              },
            },
          },
        }}
      >
        {loading ? (
          <MenuItem
            disabled
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '380px',
              py: 3,
            }}
          >
            <CircularProgress
              size={32}
              sx={{
                color: theme.palette.primary.main,
                animation: 'spin 1s linear infinite',
                '@keyframes spin': {
                  '0%': {
                    transform: 'rotate(0deg)',
                  },
                  '100%': {
                    transform: 'rotate(360deg)',
                  },
                },
              }}
            />
          </MenuItem>
        ) : notifications.length > 0 ? (
          <>
            <MenuItem
              disabled
              sx={{
                width: '380px',
                textAlign: 'center',
                fontWeight: 600,
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
                borderBottom: `1px solid ${theme.palette.divider}`,
                py: 2.5,
                backgroundColor: 'rgba(0,0,0,0.02)',
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                }}
              >
                <NotificationsIcon sx={{color: theme.palette.primary.main}} />
                <Typography variant='subtitle1'>
                  {t('notificationPageTitle')} ({notifications.length})
                </Typography>
              </Box>
            </MenuItem>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    width: '380px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    py: 2,
                    px: 2.5,
                    position: 'relative',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      transform: 'translateX(4px)',
                      '& .notification-time': {
                        opacity: 1,
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      width: 'calc(100% - 40px)', // 為刪除按鈕預留空間
                    }}
                  >
                    <ListItemText
                      primary={notification.title}
                      primaryTypographyProps={{
                        sx: {
                          fontWeight: notification.isRead ? 400 : 600,
                          color: notification.isRead
                            ? theme.palette.text.secondary
                            : theme.palette.primary.main,
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          fontSize: '0.95rem',
                          maxWidth: '100%',
                          display: 'block',
                        },
                      }}
                      sx={{
                        flex: 1,
                        overflow: 'hidden',
                        mr: 1,
                        width: '100%',
                      }}
                    />
                    <Typography
                      variant='caption'
                      className='notification-time'
                      sx={{
                        color: theme.palette.text.secondary,
                        opacity: 0.9,
                        transition: 'opacity 0.3s ease',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        fontSize: '0.8rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&::before': {
                          content: '""',
                          display: 'inline-block',
                          width: '4px',
                          height: '4px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.text.secondary,
                          opacity: 0.5,
                        },
                      }}
                    >
                      {new Date(notification.createdAt).toLocaleString()}
                    </Typography>
                  </Box>
                  <IconButton
                    size='small'
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      flexShrink: 0,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-50%) scale(1.1)',
                        color: theme.palette.error.main,
                        backgroundColor: theme.palette.error.light + '20',
                      },
                    }}
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteClick(notification);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </MenuItem>

                {index < notifications.length - 1 && (
                  <Divider
                    sx={{
                      backgroundColor: theme.palette.divider,
                      opacity: 0.3,
                      mx: 2,
                    }}
                  />
                )}
              </React.Fragment>
            ))}
          </>
        ) : (
          <MenuItem
            disabled
            sx={{
              width: '380px',
              textAlign: 'center',
              color: theme.palette.text.secondary,
              py: 4,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <NotificationsIcon sx={{fontSize: 40, opacity: 0.5}} />
              <Typography variant='body2'>
                {t('notificationPageNoNotify')}
              </Typography>
            </Box>
          </MenuItem>
        )}
      </Menu>

      <Dialog
        open={Boolean(selectedNotification)}
        onClose={() => setSelectedNotification(null)}
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            p: 3,
            maxWidth: '80rem',
            minWidth: '20rem',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <NotificationImportantIcon
              sx={{
                color: theme.palette.primary.main,
                fontSize: 32,
              }}
            />
            <Typography variant='h6' component='div'>
              {selectedNotification?.title}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setSelectedNotification(null)}
            sx={{
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'rotate(90deg)',
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{py: 3}}>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
            <Typography
              variant='body1'
              sx={{
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                color: theme.palette.text.primary,
                fontSize: '1.1rem',
                '& p': {
                  mb: 2,
                },
                '& p:last-child': {
                  mb: 0,
                },
              }}
            >
              {selectedNotification?.message}
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.5,
                color: theme.palette.text.secondary,
                mt: 3,
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: '-1.5rem',
                  left: 0,
                  right: 0,
                  height: '1px',
                  background: `linear-gradient(to right, transparent, ${theme.palette.divider}, transparent)`,
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.375,
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                  '& .MuiSvgIcon-root': {
                    fontSize: 14,
                    color: theme.palette.text.secondary,
                    opacity: 0.7,
                  },
                }}
              >
                <AccessTimeIcon />
                <Typography
                  variant='caption'
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.7rem',
                    color: theme.palette.text.secondary,
                  }}
                >
                  {selectedNotification?.createdAt &&
                    new Date(selectedNotification.createdAt).toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(selectedDeleteNotification)}
        onClose={() => setSelectedDeleteNotification(null)}
        TransitionComponent={Slide}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
            p: 3,
            maxWidth: '80rem',
            minWidth: '20rem',
            backdropFilter: 'blur(16px)',
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle sx={{pb: 2}}>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
            <DeleteIcon sx={{color: theme.palette.error.main}} />
            <Typography variant='h6'>
              {t('notificationPageConfirmDelete')}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
            <Alert severity='warning' sx={{mb: 2}}>
              {t('notificationPageDeleteWarning')}
            </Alert>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Typography variant='subtitle2' color='text.secondary'>
                {t('notificationPageDeleteTitle')}
              </Typography>
              <Typography variant='body1' sx={{fontWeight: 500}}>
                {selectedDeleteNotification?.title}
              </Typography>
            </Box>
            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
              <Typography variant='subtitle2' color='text.secondary'>
                {t('notificationPageDeleteTime')}
              </Typography>
              <Typography variant='body1'>
                {selectedDeleteNotification?.createdAt &&
                  new Date(
                    selectedDeleteNotification.createdAt,
                  ).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{px: 3, py: 2}}>
          <Button
            onClick={() => setSelectedDeleteNotification(null)}
            sx={{
              color: theme.palette.text.secondary,
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
                transform: 'translateY(-1px)',
              },
            }}
          >
            {t('cancelBtn')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color='error'
            variant='contained'
            sx={{
              boxShadow: 'none',
              transition: 'all 0.3s ease',
              '&:hover': {
                boxShadow: `0 0 16px ${theme.palette.error.main}`,
                transform: 'translateY(-1px)',
              },
            }}
          >
            {t('deleteBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={ALERT_HIDE_DURATION}
        onClose={handleCloseSnackbar}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
        TransitionComponent={Fade}
      >
        <Alert
          severity={showSnackbarType}
          variant='filled'
          sx={{
            width: '100%',
            borderRadius: '16px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
            },
          }}
        >
          {showSnackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NotificationButton;
